/*
 * PEARSON PROPRIETARY AND CONFIDENTIAL INFORMATION SUBJECT TO NDA
 * Copyright © 2026 Pearson Education, Inc.
 * All Rights Reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Pearson Education, Inc.  The intellectual and technical concepts contained
 * herein are proprietary to Pearson Education, Inc. and may be covered by U.S. and Foreign Patents,
 * patent applications, and are protected by trade secret or copyright law.
 * Dissemination of this information, reproduction of this material, and copying or distribution of this software
 * is strictly forbidden unless prior written permission is obtained
 * from Pearson Education, Inc.
 */

/* eslint-disable no-param-reassign */
import { types } from 'mobx-state-tree';
import { toJS } from 'mobx';
import { BERLIN_PIPELINE_PHASE } from '../constants/berlinBuildConstants';

const VibeFlowModel = types
  .model('VibeFlowModel', {
    builderPanelOpen: types.optional(types.boolean, false),
    canvasMode: types.optional(types.boolean, false),
    agentBuildPrompt: types.optional(types.string, ''),
    berlinDemoExchangeCount: types.optional(types.number, 0),
    berlinPipelineRunning: types.optional(types.boolean, false),
    berlinPipelinePhase: types.optional(
      types.enumeration('BerlinPipelinePhase', Object.values(BERLIN_PIPELINE_PHASE)),
      BERLIN_PIPELINE_PHASE.IDLE
    ),
    berlinStructuredSteps: types.optional(types.array(types.frozen()), []),
    berlinBuildThoughtLines: types.optional(types.array(types.string), []),
    berlinBuildThoughtCurrent: types.optional(types.string, ''),
    canvasSnapshot: types.maybeNull(types.frozen()),
    canvasHandoffApplied: types.optional(types.boolean, false)
  })
  .volatile(() => ({
    sendInstructionHandler: null
  }))
  .views((self) => ({
    get landingActive() {
      return self.builderPanelOpen && !self.canvasMode;
    },
    get snapshotForHandoff() {
      return self.landingActive ? null : self.canvasSnapshot;
    }
  }))
  .actions((self) => ({
    reset() {
      self.builderPanelOpen = false;
      self.canvasMode = false;
      self.agentBuildPrompt = '';
      self.berlinDemoExchangeCount = 0;
      self.berlinPipelineRunning = false;
      self.berlinPipelinePhase = BERLIN_PIPELINE_PHASE.IDLE;
      self.berlinStructuredSteps = [];
      self.berlinBuildThoughtLines = [];
      self.berlinBuildThoughtCurrent = '';
      self.canvasSnapshot = null;
      self.canvasHandoffApplied = false;
      self.sendInstructionHandler = null;
    },

    openBuilderPanel() {
      self.builderPanelOpen = true;
    },

    closeBuilderPanel() {
      self.builderPanelOpen = false;
    },

    openCanvas() {
      self.canvasMode = true;
      self.builderPanelOpen = false;
    },

    setAgentBuildPrompt(text) {
      self.agentBuildPrompt = text;
    },

    appendAgentBuildPrompt(updater) {
      const prev = self.agentBuildPrompt;
      self.agentBuildPrompt = typeof updater === 'function' ? updater(prev) : updater;
    },

    incrementBerlinDemoExchangeCount() {
      self.berlinDemoExchangeCount += 1;
    },

    setBerlinPipelineRunning(running) {
      self.berlinPipelineRunning = running;
    },

    setBerlinPipelinePhase(phase) {
      self.berlinPipelinePhase = phase;
    },

    setBerlinStructuredSteps(steps) {
      self.berlinStructuredSteps = steps;
    },

    mapBerlinStructuredSteps(mapFn) {
      self.berlinStructuredSteps = mapFn(toJS(self.berlinStructuredSteps));
    },

    clearBerlinBuildThought() {
      self.berlinBuildThoughtLines = [];
      self.berlinBuildThoughtCurrent = '';
    },

    setBerlinBuildThoughtCurrent(line) {
      self.berlinBuildThoughtCurrent = String(line ?? '');
    },

    finalizeBerlinBuildThoughtLine() {
      const line = self.berlinBuildThoughtCurrent.trim();
      if (line) {
        self.berlinBuildThoughtLines = [...self.berlinBuildThoughtLines, line];
      }
      self.berlinBuildThoughtCurrent = '';
    },

    replaceBerlinBuildThoughtCurrent(line) {
      self.berlinBuildThoughtCurrent = String(line ?? '');
    },

    setCanvasSnapshot(snapshot) {
      self.canvasSnapshot = snapshot;
      self.canvasHandoffApplied = false;
    },

    markCanvasHandoffApplied() {
      self.canvasHandoffApplied = true;
    },

    registerSendInstructionHandler(fn) {
      self.sendInstructionHandler = fn;
    },

    invokeSendInstruction(text) {
      if (typeof self.sendInstructionHandler !== 'function') {
        return Promise.resolve(false);
      }
      return self.sendInstructionHandler(text);
    }
  }));

export default VibeFlowModel;
